import ExcelTable from 'components/ui/excel-table';
import {
  ECellValueType,
  ExcelTableColDef,
} from 'components/ui/excel-table/types';
import FormSection from 'components/ui/form-section';
import { EQueryKeys } from 'constants/query-keys';
import { ContractAdjustmentResponseDto } from 'libs/pinnacle-sdk';

import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { ContractTabProps } from 'routes/Contracts/ContractDetail/navigation-tab/contract-config-tabs';
import { contractApi } from 'services/apis';
import { useNavigate } from 'react-router';
import { constructAdjustmentDetailPath } from 'constants/path';
import { getLocalDateForMidnightUTC } from 'utils';

type AdjustmentHistoryRow = {
  isOriginalContract?: boolean;
  adjustmentType: string;
} & Pick<
  ContractAdjustmentResponseDto,
  | 'contractAdjustmentId'
  | 'transactionDate'
  | 'documentNo'
  | 'tiv'
  | 'premiumAdjustmentAmount'
  | 'expiryDate'
  | 'shareOfPremiumSigned'
  | 'layerPremium'
  | 'ppwDueDate'
>;

const Endorsements: React.FC<ContractTabProps> = ({ contract }) => {
  const { data, isLoading } = useQuery({
    queryKey: [EQueryKeys.LIST_CONTRACT_ADJUSTMENTS, contract?.contractId],
    queryFn: () =>
      contractApi.contractListAdjustments({
        contractId: String(contract?.contractId),
      }),
    enabled: !!contract?.contractId,
  });
  const { t } = useTranslation();
  const navigate = useNavigate();

  const adjustmentHistoryData: AdjustmentHistoryRow[] = useMemo(() => {
    return [
      {
        contractAdjustmentId: '0',
        transactionDate: contract?.bindingDate ?? new Date(),
        documentNo: contract?.pinnacleReferenceNumber ?? '',
        adjustmentType: 'Original contract',
        tiv:
          contract?.totalTiv ??
          contract?.submission?.totalTivView?.totalTiv ??
          '0',
        premiumAdjustmentAmount: '0',
        layerPremium: contract?.layer?.premium ?? '0',
        shareOfPremiumSigned: (
          Number(contract?.layer?.premium) *
          Number(contract?.binderShareSigned ?? '0')
        ).toString(),
        expiryDate: getLocalDateForMidnightUTC(
          contract?.submission?.expiryDate ?? new Date()
        ).toDate(),
        ppwDueDate: getLocalDateForMidnightUTC(
          contract?.submission?.inceptionDate ?? new Date()
        )
          .add(Number(contract?.premiumPaymentWarranty ?? '0'), 'day')
          .toDate(),
        isOriginalContract: true,
      },
      ...(data ?? []),
    ];
  }, [data, contract]);

  const columnDef = useMemo<ExcelTableColDef<AdjustmentHistoryRow>[]>(() => {
    return [
      {
        headerName: t('contractAdjustment.headers.effectiveDate'),
        field: 'transactionDate',
        cellRenderer: (params: any) =>
          format(new Date(params.value), 'dd/MM/yyyy'),
        editable: false,
      },
      {
        headerName: t('contractAdjustment.headers.documentNo'),
        field: 'documentNo',
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAdjustment.headers.adjustmentType'),
        field: 'adjustmentType',
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAdjustment.headers.tiv'),
        field: 'tiv',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
        editable: false,
        resizable: true,
      },
      {
        headerName: t(
          'contractAdjustment.headers.layerPremiumAdjustmentAmountProrated'
        ),
        field: 'premiumAdjustmentAmount',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
        editable: false,
        resizable: true,
      },
      {
        headerName: t('contractAdjustment.headers.layerPremium'),
        field: 'layerPremium',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('contractAdjustment.headers.shareOfPremiumSigned'),
        field: 'shareOfPremiumSigned',
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
        resizable: true,
        editable: false,
      },
      {
        headerName: t('contractAdjustment.headers.expiryDate'),
        field: 'expiryDate',
        cellRenderer: (params: any) =>
          format(new Date(params.value), 'dd/MM/yyyy'),
        editable: false,
      },
      {
        headerName: t('contractAdjustment.headers.ppwDueDate'),
        field: 'ppwDueDate',
        cellRenderer: (params: any) =>
          format(new Date(params.value), 'dd/MM/yyyy'),
        editable: false,
      },
    ];
  }, [t]);

  return (
    <>
      <FormSection title={t('contract.adjustmentHistory')} collapsible>
        <ExcelTable
          data={adjustmentHistoryData}
          columnDefs={columnDef}
          includeIdRow={false}
          rowKeyColName="contractAdjustmentId"
          tableContainerClassName="h-[24rem]"
          loading={isLoading}
          onRowClicked={(row) => {
            if (row.data?.isOriginalContract) {
              return;
            }
            navigate(
              constructAdjustmentDetailPath(
                String(contract?.contractId),
                String(row.data?.contractAdjustmentId)
              )
            );
          }}
        />
      </FormSection>
    </>
  );
};

export default Endorsements;
